/*
 * Featured Collections.
 *
 * A tabbed set of collection carousels. Each tab owns one panel, and each
 * panel owns its own Swiper (built lazily the first time the panel is shown,
 * since Swiper cannot measure a hidden element). Autoplay only ever runs on
 * the panel that is currently visible. Requires swiper-bundle.min.js, which
 * the section loads first.
 */
if (!customElements.get('featured-collections')) {
  customElements.define(
    'featured-collections',
    class FeaturedCollections extends HTMLElement {
      connectedCallback() {
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.tabs = Array.from(this.querySelectorAll('.featured-collections__tab'));
        this.panels = Array.from(this.querySelectorAll('.featured-collections__panel'));
        if (this.panels.length === 0) return;

        this.loop = this.dataset.loop === 'true';
        this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.reduceMotion.matches;
        this.autoplayDelay = (parseFloat(this.dataset.autoplaySpeed) || 5) * 1000;
        this.showArrows = this.dataset.arrows !== 'false';
        this.showScrollbar = this.dataset.scrollbar !== 'false';

        // One Swiper instance per panel, filled in on first activation.
        this.swipers = new Array(this.panels.length).fill(null);
        this.activeIndex = 0;

        this.onTabClick = this.onTabClick.bind(this);
        this.onTabKeydown = this.onTabKeydown.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onTabClick);
          tab.addEventListener('keydown', this.onTabKeydown);
        });

        document.addEventListener('shopify:block:select', this.onBlockSelect);

        // Bring the first panel online.
        this.activate(0, { focusTab: false });
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.tabs.forEach((tab) => {
          tab.removeEventListener('click', this.onTabClick);
          tab.removeEventListener('keydown', this.onTabKeydown);
        });
        this.swipers.forEach((swiper) => swiper && swiper.destroy(true, true));
        this.swipers = [];
      }

      /* Carousel ---------------------------------------------------------- */

      buildSwiper(index) {
        const panel = this.panels[index];
        const container = panel.querySelector('.swiper');
        if (!container) return null;

        const slideCount = container.querySelectorAll('.swiper-slide').length;
        if (typeof Swiper === 'undefined' || slideCount === 0) return null;

        return new Swiper(container, {
          // Below 990px the cards are a fixed 180px wide (section-featured-collections.css).
          slidesPerView: 'auto',
          spaceBetween: 12,
          speed: this.reduceMotion.matches ? 0 : 500,
          loop: this.loop && slideCount > 2,
          watchOverflow: true,
          grabCursor: true,
          a11y: { enabled: true },
          keyboard: { enabled: true, onlyInViewport: true },
          // Wheel / trackpad scrolling. forceToAxis keeps a plain vertical
          // page scroll from being hijacked; releaseOnEdges hands the scroll
          // back to the page once the track bottoms out at either end.
          mousewheel: { forceToAxis: true, releaseOnEdges: true },
          navigation: this.showArrows
            ? {
                prevEl: panel.querySelector('.featured-collections__arrow--prev'),
                nextEl: panel.querySelector('.featured-collections__arrow--next'),
              }
            : false,
          scrollbar: this.showScrollbar
            ? {
                el: panel.querySelector('.featured-collections__scrollbar'),
                draggable: true,
              }
            : false,
          autoplay: this.autoplayEnabled
            ? { delay: this.autoplayDelay }
            : false,
          breakpoints: {
            750: { slidesPerView: 'auto', spaceBetween: 16 },
            990: { slidesPerView: 4, spaceBetween: 20 },
            1200: { slidesPerView: 5, spaceBetween: 20 },
          },
        });
      }

      startAutoplay(index) {
        const swiper = this.swipers[index];
        if (!swiper || !this.autoplayEnabled || !swiper.autoplay) return;
        swiper.autoplay.start();
      }

      stopAutoplay(index) {
        const swiper = this.swipers[index];
        if (swiper && swiper.autoplay) swiper.autoplay.stop();
      }

      /* Tabs ------------------------------------------------------------- */

      activate(index, { focusTab = true } = {}) {
        if (index < 0 || index >= this.panels.length) return;

        this.panels.forEach((panel, i) => {
          const selected = i === index;
          panel.hidden = !selected;
          panel.classList.toggle('is-active', selected);
          if (!selected) this.stopAutoplay(i);
        });

        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.classList.toggle('is-active', selected);
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
        });

        this.activeIndex = index;

        // Build on first view; otherwise re-measure (it may have grown a
        // scrollbar or changed breakpoint while hidden).
        if (!this.swipers[index]) {
          this.swipers[index] = this.buildSwiper(index);
        } else {
          this.swipers[index].update();
        }

        this.startAutoplay(index);

        if (focusTab && this.tabs[index]) this.tabs[index].focus();
      }

      onTabClick(event) {
        const index = this.tabs.indexOf(event.currentTarget);
        if (index !== -1 && index !== this.activeIndex) this.activate(index, { focusTab: false });
      }

      onTabKeydown(event) {
        const current = this.tabs.indexOf(event.currentTarget);
        if (current === -1) return;

        let next = null;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = (current + 1) % this.tabs.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = (current - 1 + this.tabs.length) % this.tabs.length;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = this.tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        this.activate(next);
      }

      /* Theme editor: jump to the collection block being edited. */
      onBlockSelect(event) {
        const panel = event.target.closest && event.target.closest('.featured-collections__panel');
        if (!panel || !this.contains(panel)) return;
        const index = this.panels.indexOf(panel);
        if (index !== -1) this.activate(index, { focusTab: false });
      }
    }
  );
}
